/**
 * Данные эпик-вью: дерево детей, их состояние и что происходило в GitLab.
 *
 * Всё поддерево эпика приезжает одним запросом и раскладывается по узлам
 * здесь. Уровней два: истории эпика и их подзадачи — глубже иерархия не бывает
 * по правилам типов.
 */
import {
  listActions,
  listStands,
  toReleases,
  type FeatureStand,
  type Release,
} from '@/features/devops/api'
import { issueApi } from '@/api'
import { listSubtree } from '@/features/issue/links'
import { toRow } from '@/features/issues/api'
import { getSummary, type SummaryRow } from '@/features/pipelines/api'

import type { IssueRow } from '@/features/issues/api'
import type { ProjectColumn } from '@/features/projects/api'

/** Узел дерева: ребёнок эпика и его подзадачи */
export interface EpicNode {
  issue: IssueRow
  children: IssueRow[]
}

/**
 * Дерево эпика — из одного плоского списка поддерева.
 *
 * Уровней в дереве два: задачи эпика и их подзадачи. Глубже иерархия не
 * бывает по правилам типов, поэтому всё, что не легло в свой узел, показываем
 * первым уровнем — иначе задача пропала бы с экрана из-за того, что её
 * родитель оказался неизвестно где.
 *
 * Задачи без родителя вовсе — отбрасываем: в поддереве эпика их быть не может.
 * Так экран переживает встречу со старым сервисом, который фильтр по предку не
 * знает и молча отдаёт весь проект (ошибки при этом нет — gateway пропускает
 * незнакомый параметр).
 */
export const toTree = (issues: IssueRow[], epicId: string): EpicNode[] => {
  const nodes = new Map<string, EpicNode>()

  for (const issue of issues) {
    if (issue.parentId === epicId) nodes.set(issue.id, { issue, children: [] })
  }

  const orphans: IssueRow[] = []

  for (const issue of issues) {
    if (issue.parentId === epicId || !issue.parentId) continue

    const parent = nodes.get(issue.parentId)

    if (parent) {
      parent.children.push(issue)
    } else {
      orphans.push(issue)
    }
  }

  for (const issue of orphans) {
    nodes.set(issue.id, { issue, children: [] })
  }

  return [...nodes.values()]
}

/**
 * Поддерево берётся одним запросом: обход по уровням стоил запроса на каждую
 * задачу эпика и выбирал лимит проверки прав целиком (DEV-1424).
 */
export const loadTree = async (projectId: string, epicId: string): Promise<EpicNode[]> =>
  toTree(await listSubtree(projectId, epicId), epicId)

/** Все задачи эпика плоским списком — по ним считается прогресс */
export const flatten = (nodes: EpicNode[]): IssueRow[] =>
  nodes.flatMap((node) => [node.issue, ...node.children])

/** Разбивка задач эпика по категориям статусов — сегменты полосы прогресса */
export interface Progress {
  total: number
  done: number
  inProgress: number
  open: number
}

/**
 * Считаем по категории колонки, а не по её названию: «Готово» в одном проекте
 * зовётся «Выкачено», и сравнение строк врало бы у всех, кто переименовал
 * колонку.
 */
export const toProgress = (issues: IssueRow[], columns: ProjectColumn[]): Progress => {
  const categoryOf = (issue: IssueRow) =>
    columns.find((column) => column.id === issue.columnId)?.statusCategory ?? ''

  const done = issues.filter((issue) => categoryOf(issue) === 'STATUS_CATEGORY_DONE').length
  const inProgress = issues.filter(
    (issue) => categoryOf(issue) === 'STATUS_CATEGORY_IN_PROGRESS',
  ).length

  return { total: issues.length, done, inProgress, open: issues.length - done - inProgress }
}

/**
 * Последний прогон на каждый сервис эпика. Сводка отдаёт по одному прогону на
 * пару «сервис и ветка» — здесь сворачиваем до сервиса: на эпик смотрят, чтобы
 * понять, где красное, а не читать все ветки.
 */
export const byService = (rows: SummaryRow[]): SummaryRow[] => {
  const latest = new Map<string, SummaryRow>()

  for (const row of rows) {
    const current = latest.get(row.run.projectPath)

    if (!current) {
      latest.set(row.run.projectPath, row)

      continue
    }

    // Упавшее держим до последнего: сервис с красным прогоном остаётся
    // красным, даже если следом прошёл зелёный прогон другой ветки.
    if (current.run.status === 'failed') continue

    const newer = new Date(row.run.createdAt).getTime() > new Date(current.run.createdAt).getTime()

    if (row.run.status === 'failed' || newer) latest.set(row.run.projectPath, row)
  }

  return [...latest.values()]
}

export const loadPipelines = async (projectId: string, epicId: string): Promise<SummaryRow[]> =>
  byService(await getSummary(projectId, epicId))

/** Стенды и релизы эпика */
export interface EpicOps {
  stands: FeatureStand[]
  releases: Release[]
}

/**
 * Живые стенды берём у GitLab, релизы — из журнала.
 *
 * Стенд мог сняться авто-стопом или мержем MR, и журнал об этом не знает: он
 * помнит нажатую кнопку, а не состояние кластера (DEV-1627). Отказ ручки
 * стендов не роняет блок — релизы эпика полезны и сами по себе.
 */
export const loadOps = async (issueIds: string[]): Promise<EpicOps> => {
  const own = new Set(issueIds)
  const [stands, actions] = await Promise.all([
    listStands().catch(() => []),
    listActions(),
  ])

  return {
    stands: stands.filter((stand) => own.has(stand.issueId)),
    releases: toReleases(actions.filter((action) => own.has(action.issueId))).slice(0, 3),
  }
}

/** Строка экрана эпиков: сам эпик и готовность по его задачам */
export interface EpicRow {
  issue: IssueRow
  progress: Progress
  /** Все задачи эпика доехали до тестирования — эпик можно смотреть целиком */
  isTesting: boolean
}

/**
 * Колонка тестирования. Своего признака у колонки нет — есть только имя и
 * категория, а категория у «В работе» и «Тестирования» одна. Поэтому смотрим
 * на имя: в проектах трекера эта колонка называется Testing или
 * «Тестирование», и другого способа отличить её от «В работе» модель не даёт.
 */
const isTestColumn = (name: string) => {
  const lower = name.toLowerCase()

  return lower.includes('test') || lower.includes('тест')
}

/** Задачи эпика, разложенные по колонкам: идентификатор колонки → сколько */
type ColumnCounts = Record<string, number>

/** Разбивка по категориям — из раскладки по колонкам */
const countsToProgress = (counts: ColumnCounts, columns: ProjectColumn[]): Progress => {
  const sumOf = (category: string) =>
    columns
      .filter((column) => column.statusCategory === category)
      .reduce((total, column) => total + (counts[column.id] ?? 0), 0)

  const total = Object.values(counts).reduce((sum, count) => sum + count, 0)
  const done = sumOf('STATUS_CATEGORY_DONE')
  const inProgress = sumOf('STATUS_CATEGORY_IN_PROGRESS')

  return { total, done, inProgress, open: total - done - inProgress }
}

/**
 * Эпик готов к общему стенду, когда каждая его задача либо в тестировании,
 * либо уже закрыта, и хотя бы одна ждёт проверки. Пока хоть одна в работе,
 * собирать эпик рано: половина веток ещё не влита, и стенд покажет
 * незаконченное (DEV-1633).
 */
const countsReadyForStand = (counts: ColumnCounts, columns: ProjectColumn[]): boolean => {
  const testing = columns
    .filter((column) => isTestColumn(column.name))
    .reduce((total, column) => total + (counts[column.id] ?? 0), 0)

  const { total, done } = countsToProgress(counts, columns)

  return testing > 0 && testing + done === total
}

/**
 * Эпики проекта с готовностью каждого.
 *
 * Раскладку по колонкам считает сервис одним обходом дерева. Раньше экран
 * собирал её сам: выкачивал задачи проекта страницами с потолком в тысячу — и
 * всё, что дальше, пропадало вместе с эпиками, молча и незаметно (DEV-1635).
 *
 * Готовность считается здесь, а не на бэкенде: «в тестировании» узнаётся по
 * имени колонки, а имена колонок — дело доски.
 */
export const loadEpics = async (
  projectId: string,
  columns: ProjectColumn[],
): Promise<EpicRow[]> => {
  const { data } = await issueApi.issueServiceListEpics(projectId)

  const isDone = (issue: IssueRow) =>
    columns.find((column) => column.id === issue.columnId)?.statusCategory ===
    'STATUS_CATEGORY_DONE'

  return (data.epics ?? [])
    .filter((row) => row.epic)
    .map((row) => {
      // uint64 приезжает строкой: JSON-числа не хранят его целиком, и gateway
      // отдаёт его строкой у всех счётчиков.
      const counts: ColumnCounts = Object.fromEntries(
        Object.entries(row.columnCounts ?? {}).map(([columnId, count]) => [
          columnId,
          Number(count),
        ]),
      )

      return {
        issue: toRow(row.epic!),
        progress: countsToProgress(counts, columns),
        isTesting: countsReadyForStand(counts, columns),
      }
    })
    // Закрытые эпики уходят вниз: экран открывают ради того, что в работе.
    .sort((left, right) => Number(isDone(left.issue)) - Number(isDone(right.issue)))
}
